import type { TrainRenderProfile } from './trainRenderLod'
import {
  r160CarPlacards,
  type R160Placement,
} from './r160Model'

export type R160DecalMinimumTier = 'route' | 'full'

export type R160DecalPolicy =
  | {
      classification: 'dynamic'
      minimumTier: R160DecalMinimumTier
    }
  | {
      classification: 'static'
      minimumTier: 'full'
      /** Agent 5 may remove this per-frame datum after the marking exists in the GLB. */
      bakeCandidate: true
    }

/**
 * Exhaustive inventory of the placards currently emitted by `r160CarPlacards`.
 *
 * Flags and MTA roundels do not change with service data. They remain full-tier
 * placements only until equivalent markings are baked into the close model assets.
 * Everything else is dynamic: route/destination signs are essential at the route
 * tier, while car identity and live cab-light state require the full tier.
 */
export const R160_DECAL_POLICY = {
  'side-sign': { classification: 'dynamic', minimumTier: 'route' },
  'front-bullet': { classification: 'dynamic', minimumTier: 'route' },
  'car-number': { classification: 'dynamic', minimumTier: 'full' },
  'tail-lamp': { classification: 'dynamic', minimumTier: 'full' },
  'lamp-off': { classification: 'dynamic', minimumTier: 'full' },
  flag: { classification: 'static', minimumTier: 'full', bakeCandidate: true },
  mta: { classification: 'static', minimumTier: 'full', bakeCandidate: true },
} as const satisfies Record<R160Placement['kind'], R160DecalPolicy>

export type R160DecalCar = {
  /** Zero-based position in the complete consist. */
  carIndex: number
  routeId: string
  destination: string
  carNumber: string
}

export type R160DecalContent =
  | { kind: 'route-destination'; routeId: string; destination: string }
  | { kind: 'route'; routeId: string }
  | { kind: 'car-number'; carNumber: string }
  | { kind: 'service-state'; state: 'tail-lamp' | 'lamp-off' }
  | { kind: 'static'; asset: 'flag' | 'mta' }

export type R160DecalDatum<CarT extends R160DecalCar = R160DecalCar> = {
  /** Preserve the caller's car object so the renderer can read position and bearing. */
  car: CarT
  placement: R160Placement
  content: R160DecalContent
}

/** Shared result for paths that must not create any per-car sticker data. */
export const EMPTY_R160_DECAL_DATUMS: readonly R160DecalDatum[] = Object.freeze([])

/**
 * Which placards are DISPLAYS rather than markings.
 *
 * The distinction earns its keep because the two are drawn under different rules. A
 * display is a lit dot matrix whose content is live service data, so it is regenerated
 * as a canvas per (route, destination) and it is the placard drawn at the MOST tiers —
 * `minimumTier: 'route'` above, where everything else needs `'full'`. That combination
 * is what made signs the expensive half of the sticker budget, so they are restricted to
 * the followed vehicle (see the note in `buildVehicleLayers`).
 *
 * ⚠️ Everything NOT in here — the car number, the US flag, the MTA roundel, the tail and
 * blacked-out cab lamps — stays on the WHOLE FLEET. Those are what makes an R160 look
 * like an R160 at the zooms the mesh is drawn at, they are static or near-static art
 * rather than per-frame canvas work, and the LOD policy already keeps them to the close
 * tier. Removing them fleet-wide was tried and it is not the change anyone wanted.
 */
export const R160_SIGN_PLACARDS = new Set<R160Placement['kind']>(['side-sign', 'front-bullet'])

export function r160PlacementIsSign(kind: R160Placement['kind']): boolean {
  return R160_SIGN_PLACARDS.has(kind)
}

/** Shared placement gate for allocating selectors and pooled renderer integrations. */
export function r160PlacementEligibleForProfile(
  profile: TrainRenderProfile,
  placement: R160Placement,
): boolean {
  if (profile.geometry === 'marker' || profile.decals === 'none') return false
  const policy = R160_DECAL_POLICY[placement.kind]
  return profile.decals === 'full' || policy.minimumTier === 'route'
}

function contentFor<CarT extends R160DecalCar>(
  kind: R160Placement['kind'],
  car: CarT,
): R160DecalContent {
  if (kind === 'side-sign') {
    return {
      kind: 'route-destination',
      routeId: car.routeId,
      destination: car.destination,
    }
  }
  if (kind === 'front-bullet') return { kind: 'route', routeId: car.routeId }
  if (kind === 'car-number') return { kind: 'car-number', carNumber: car.carNumber }
  if (kind === 'tail-lamp' || kind === 'lamp-off') {
    return { kind: 'service-state', state: kind }
  }
  return { kind: 'static', asset: kind }
}

/**
 * Select decal datums from an already-resolved per-train render profile.
 *
 * This function deliberately has no camera, projection, zoom, or distance input. The
 * geometry policy resolves those once per train and this selector only applies its
 * `geometry`/`decals` decision to the consist. Marker mode returns no mesh decals: its
 * route bullet is the renderer's one marker datum, so expanding eight cars here would
 * violate the marker contract even though the shared profile says `decals: 'route'`.
 */
export function selectR160Decals<CarT extends R160DecalCar>(
  profile: TrainRenderProfile,
  cars: readonly CarT[],
): readonly R160DecalDatum<CarT>[] {
  if (profile.geometry === 'marker' || profile.decals === 'none') {
    return EMPTY_R160_DECAL_DATUMS as readonly R160DecalDatum<CarT>[]
  }

  const selected: R160DecalDatum<CarT>[] = []
  const carCount = cars.length
  for (const car of cars) {
    for (const placement of r160CarPlacards(car.carIndex, carCount)) {
      if (!r160PlacementEligibleForProfile(profile, placement)) continue
      selected.push({ car, placement, content: contentFor(placement.kind, car) })
    }
  }
  return selected
}
